import { motion } from "framer-motion";
import { Activity, Brain, HeartPulse } from "lucide-react";

const specialties = [
  {
    id: "parkinsons",
    icon: Activity,
    eyebrow: "01 — Parkinson's Disease",
    title: "Voice, swallow & movement-related communication.",
    body: "Specialised intervention including SPEAK OUT!® for voice preservation, dysarthria management, and dysphagia evaluation to support safe eating, drinking and confident communication.",
    image:
      "https://images.pexels.com/photos/5206923/pexels-photo-5206923.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "lg:col-span-7 lg:row-span-2",
    tall: true,
  },
  {
    id: "cognitive",
    icon: Brain,
    eyebrow: "02 — Cognitive Issues",
    title: "Memory, attention & executive function.",
    body: "Therapy for adults experiencing cognitive change due to dementia, traumatic brain injury or chronic illness — restoring strategies for daily independence.",
    image:
      "https://images.pexels.com/photos/36961779/pexels-photo-36961779.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "lg:col-span-5",
  },
  {
    id: "cva",
    icon: HeartPulse,
    eyebrow: "03 — Post-CVA Rehabilitation",
    title: "Stroke recovery: aphasia, apraxia & dysarthria.",
    body: "Targeted rehabilitation following cerebrovascular accident — rebuilding language, motor speech and swallowing function with measurable, person-centred goals.",
    image: null,
    span: "lg:col-span-5",
  },
];

export const Specialties = () => {
  return (
    <section
      id="specialties"
      data-testid="specialties-section"
      className="relative py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-4">
              Areas of Specialty
            </p>
            <h2
              data-testid="specialties-title"
              className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#2D332F] max-w-3xl leading-tight"
            >
              Three pillars of neurological speech &amp; language care.
            </h2>
          </div>
          <p className="text-[#5C6661] max-w-sm leading-relaxed">
            Every plan is shaped around your story, your strengths and the
            outcomes that matter to your daily life.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 auto-rows-fr">
          {specialties.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.id}
                data-testid={`specialty-card-${s.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className={`relative overflow-hidden rounded-3xl border border-black/5 bg-[#FDFCF8] shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-lg transition-all duration-500 ${s.span}`}
              >
                {s.image && s.tall && (
                  <div className="relative h-64 lg:h-80 w-full overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2D332F]/40 to-transparent" />
                  </div>
                )}

                <div className="p-8 md:p-10 flex flex-col h-full">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-[#F3F1E7] flex items-center justify-center">
                      <Icon
                        size={18}
                        strokeWidth={1.5}
                        className="text-[#739072]"
                      />
                    </div>
                    <span className="text-xs uppercase tracking-[0.2em] text-[#5C6661]">
                      {s.eyebrow}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl font-light tracking-tight text-[#2D332F] leading-tight mb-4">
                    {s.title}
                  </h3>
                  <p className="text-[#5C6661] leading-relaxed">{s.body}</p>
                </div>

                {s.image && !s.tall && (
                  <div className="absolute -right-12 -bottom-12 w-48 h-48 rounded-full overflow-hidden opacity-30">
                    <img
                      src={s.image}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
